import { useMutation, useQuery } from 'react-query';
import { api } from '../services/api';
import Modal from './ui/Modal';
import Button from './ui/Button';
import Pill from './ui/Pill';
import { ErrorNotice, InfoNotice, LoadingPanel } from './ui/States';
import { errorMessage } from '../lib/printable';
import { PreparedMessage } from '../lib/stipends';

interface SendResult {
  messages: { id: string; to: string; role: string; status: string; reason: string | null }[];
  stepCompleted: boolean;
  outbox: boolean;
}

/** Preview a prepared stipend email, then send it on the operator's click. */
export default function StipendMessage({
  kind,
  participantId,
  agreementId,
  onClose,
  onSent,
}: {
  kind: string;
  participantId?: string;
  agreementId?: string;
  onClose: () => void;
  onSent?: () => void;
}) {
  const prepared = useQuery<PreparedMessage>(
    ['stipend-message', kind, participantId, agreementId],
    async () =>
      (await api.get(`/stipends/messages/${kind}/preview`, { params: { participantId, agreementId } })).data.data,
  );
  const send = useMutation(
    async () =>
      (await api.post('/stipends/messages/send', { kind, participantId, agreementId })).data.data as SendResult,
    { onSuccess: () => onSent?.() },
  );
  const p = prepared.data;
  return (
    <Modal
      title={p ? p.label : 'Message'}
      width={720}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-3 w-full">
          <Button variant="ghost" onClick={onClose}>
            {send.data ? 'Close' : 'Cancel'}
          </Button>
          {!send.data ? (
            <Button variant="primary" disabled={!p?.preview || send.isLoading} onClick={() => send.mutate()}>
              {send.isLoading ? 'Sending…' : p?.outbox ? 'Log in the outbox' : `Send to ${p?.recipients.length ?? 0}`}
            </Button>
          ) : null}
        </div>
      }
    >
      {prepared.isLoading ? <LoadingPanel label="Preparing…" height={160} /> : null}
      {prepared.error ? (
        <ErrorNotice>{errorMessage(prepared.error, 'Could not prepare the message.')}</ErrorNotice>
      ) : null}
      {p ? (
        <div className="space-y-3 text-table">
          {p.outbox ? (
            <InfoNotice>Outbox mode is on: the message is logged as not sent and nobody receives it.</InfoNotice>
          ) : null}
          {p.problems.length ? <ErrorNotice>{p.problems.join('. ')}.</ErrorNotice> : null}
          <div>
            <div className="text-small text-ink-2 mb-1">To</div>
            {p.recipients.length ? (
              p.recipients.map((r) => (
                <div key={r.email}>
                  {r.name ? `${r.name} · ` : ''}
                  {r.email} <span className="text-ink-2">({r.role})</span>
                </div>
              ))
            ) : (
              <div className="text-ink-2">Nobody yet</div>
            )}
          </div>
          {p.preview ? (
            <>
              <div>
                <div className="text-small text-ink-2 mb-1">Subject</div>
                <div>{p.preview.subject}</div>
              </div>
              <pre className="whitespace-pre-wrap font-sans text-small bg-paper border border-line p-3 max-h-[340px] overflow-auto">
                {p.preview.text}
              </pre>
            </>
          ) : null}
          {send.error ? <ErrorNotice>{errorMessage(send.error, 'Could not send.')}</ErrorNotice> : null}
          {send.data ? (
            <div className="space-y-1">
              {send.data.messages.map((m) => (
                <div key={m.id} className="flex items-center gap-2">
                  <Pill tone={m.status === 'sent' ? 'healthy' : m.status === 'not_sent' ? 'planned' : 'failed'}>
                    {m.status.replace('_', ' ')}
                  </Pill>
                  {m.to}
                  {m.reason ? <span className="text-ink-2"> · {m.reason}</span> : null}
                </div>
              ))}
              {send.data.stepCompleted ? (
                <div className="text-ink-2 text-small">The checklist step was closed.</div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}
